/**
 * 菜单状态管理
 * 管理导航菜单、当前选中项、最近访问等
 */
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { MAX_RECENT_MENU_COUNT } from '../const/constants.js';

/**
 * @typedef {Object} MenuItem
 * @property {string} action - 菜单动作标识
 * @property {string} title - 菜单标题
 * @property {string} menuKey - 所属菜单分类
 * @property {Array} children - 子菜单项
 */

/**
 * @typedef {Object} MenuState
 * @property {string} action - 当前选中的菜单 action
 * @property {MenuItem|null} currentMenu - 当前选中的菜单配置
 * @property {Array<MenuItem>} navItems - 导航菜单列表
 * @property {Array<MenuItem>} recentMenu - 最近访问的菜单列表
 * @property {boolean} isMenuCollapsed - 菜单是否折叠
 * @property {Array<string>} expandedKeys - 展开的菜单 key 列表
 */

const initialState = {
  action: '',
  currentMenu: null,
  navItems: [],
  recentMenu: [],
  isMenuCollapsed: false,
  expandedKeys: [],
};

export const useMenuStore = create(
  devtools(
    (set, get) => ({
      ...initialState,

      // ========== Actions ==========

      /**
       * 设置当前选中的菜单 action
       * @param {string} action
       */
      setAction: (action) => set({ action }, false, 'menu/setAction'),

      /**
       * 设置当前菜单配置
       * @param {MenuItem} menu
       */
      setCurrentMenu: (menu) => set(
        { 
          currentMenu: menu, 
          action: menu?.action || '',
          expandedKeys: menu?.menuKey ? [menu.menuKey, 'recent'] : ['recent']
        }, 
        false, 
        'menu/setCurrentMenu'
      ),

      /**
       * 设置导航菜单列表
       * @param {Array<MenuItem>} items
       */
      setNavItems: (items) => set({ navItems: items }, false, 'menu/setNavItems'),

      /**
       * 设置最近访问菜单
       * @param {Array<MenuItem>} items
       */
      setRecentMenu: (items) => set({ recentMenu: items }, false, 'menu/setRecentMenu'),

      /**
       * 添加最近访问菜单（去重，最多保留4个）
       * @param {string} menuAction
       */
      addRecentMenu: (menuAction) => {
        const { navItems, recentMenu } = get();
        const allMenus = navItems.flatMap(item => item.children || []);
        const targetMenu = allMenus.find(item => item.action === menuAction);
        
        if (targetMenu) {
          const filtered = recentMenu.filter(item => item.action !== menuAction);
          const newRecent = [targetMenu, ...filtered].slice(0, MAX_RECENT_MENU_COUNT);
          set({ recentMenu: newRecent }, false, 'menu/addRecentMenu');
        }
      },

      /**
       * 切换菜单折叠状态
       */
      toggleMenuCollapsed: () => set(
        (state) => ({ isMenuCollapsed: !state.isMenuCollapsed }),
        false,
        'menu/toggleMenuCollapsed'
      ),

      /**
       * 设置菜单折叠状态
       * @param {boolean} collapsed
       */
      setMenuCollapsed: (collapsed) => set(
        { isMenuCollapsed: collapsed },
        false,
        'menu/setMenuCollapsed'
      ),

      /**
       * 设置展开的菜单 keys
       * @param {Array<string>} keys
       */
      setExpandedKeys: (keys) => set({ expandedKeys: keys }, false, 'menu/setExpandedKeys'),

      /**
       * 根据 action 查找并设置当前菜单
       * @param {string} action
       */
      findAndSetCurrentMenu: (action) => {
        const { navItems } = get();
        const allMenus = navItems.flatMap(item => item.children || []);
        const targetMenu = allMenus.find(item => item.action === action);
        
        if (targetMenu) {
          set({
            currentMenu: targetMenu,
            action: targetMenu.action,
            expandedKeys: [targetMenu.menuKey, 'recent']
          }, false, 'menu/findAndSetCurrentMenu');
        }
      },

      /**
       * 重置菜单状态
       */
      resetMenu: () => set(initialState, false, 'menu/reset'),
    }),
    { name: 'MenuStore' }
  )
);
